import translations from "@/i18n/messages.json";
import Price from "@/services/price";
import Utils from "@/services/utils";

/**
 * Centralise l'affichage des prix sur l'order page a partir des champs
 * Salesforce (centimes). Voir la matrice de decision dans le plan FORGE-652.
 */
export default class PriceDisplay {
  static shouldShowIntroOffer(choice) {
    return (
      choice &&
      Object.prototype.hasOwnProperty.call(choice, "startingPrice") &&
      Number(choice.startingPriceDuration) > 0 &&
      (choice.isSingleProduct ? this.hasAfterLine(choice) : true)
    );
  }

  static isFreeStartingPrice(choice) {
    const startingPrice = choice?.startingPrice;
    return (
      startingPrice === 0 ||
      startingPrice === null ||
      startingPrice === undefined
    );
  }

  static hasAfterLine(choice) {
    if (!choice) return false;

    const defaultPrice = choice.defaultPrice ?? 0;
    if (defaultPrice !== 0) {
      return true;
    }

    return Boolean(String(choice.freeLabel || "").trim());
  }

  static buildAfterLine(choice, lang) {
    if (!this.hasAfterLine(choice)) {
      return null;
    }

    const t = translations[lang]?.labels || translations.fr.labels;
    const defaultPrice = choice.defaultPrice ?? 0;

    if (defaultPrice !== 0) {
      const renewalDuration = parseInt(choice.renewalTermLength, 10);
      return {
        thenLabel: t.then,
        amount: Price.format(defaultPrice),
        renewalDuration:
          renewalDuration > 0
            ? Utils.displayDuration(renewalDuration, lang)
            : "",
        freeLabel: null,
      };
    }

    return {
      thenLabel: t.then,
      amount: null,
      renewalDuration: "",
      freeLabel: String(choice.freeLabel || "").trim(),
    };
  }

  static buildStartingLine(choice, lang) {
    const duration = Number(choice.startingPriceDuration);

    if (this.isFreeStartingPrice(choice)) {
      return {
        isFree: true,
        amount: null,
        durationText: Utils.displayFreeDuration(duration, lang),
      };
    }

    const showDuration = this.hasAfterLine(choice);

    return {
      isFree: false,
      amount: Price.format(choice.startingPrice),
      durationText: showDuration
        ? Utils.displayDuration(duration, lang, true)
        : "",
    };
  }

  static buildSimpleAmount(choice) {
    return Price.format(choice.unitPrice);
  }

  static freeDaysCount(choice) {
    const n = Number(choice?.freeDays);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  static buildFreeDaysMention(choice, lang = "fr") {
    const t = translations[lang]?.labels || translations.fr.labels;
    const n = this.freeDaysCount(choice);
    if (!n) {
      return null;
    }
    if (n === 1) {
      return t.freeDaysMentionSingular;
    }
    return String(t.freeDaysMention || "").replace("{n}", String(n));
  }

  static resolvePromotionLabel(choice, lang = "fr") {
    const promo = String(choice?.promotionLabel || "").trim();
    if (promo) {
      return promo;
    }
    return this.buildFreeDaysMention(choice, lang);
  }

  static isTrialMention(choice) {
    if (this.freeDaysCount(choice) > 0) {
      return true;
    }
    const promo = String(choice?.promotionLabel || "");
    return /\d\s*.{0,24}(?:premiers?\s+)?(?:jours?|days?|d[ií]as?)\b/i.test(
      promo,
    );
  }

  static buildPriceViewModel(choice, lang = "fr", options = {}) {
    const compact = Boolean(options?.compact);

    if (!choice) {
      return {
        mode: "simple",
        promotionLabel: null,
        oldPrice: null,
        simpleAmount: "",
        frequency: null,
        intro: null,
      };
    }

    const base = {
      promotionLabel: compact ? null : this.resolvePromotionLabel(choice, lang),
      oldPrice:
        compact || !choice.oldPrice ? null : Price.format(choice.oldPrice),
      frequency: choice.frequency || null,
    };

    if (!this.shouldShowIntroOffer(choice)) {
      return {
        ...base,
        mode: "simple",
        simpleAmount: this.buildSimpleAmount(choice),
        intro: null,
      };
    }

    const afterLine = this.buildAfterLine(choice, lang);

    return {
      ...base,
      mode: "intro",
      simpleAmount: null,
      intro: {
        starting: this.buildStartingLine(choice, lang),
        after: afterLine,
      },
    };
  }
}
